<template>
  <b-modal
    :id="modalId"
    no-close-on-backdrop
    size="xl"
    @shown="init()"
    @hidden="hideModal"
  >
    <template v-slot:modal-header>
      <div>
        <h5 class="modal-title">{{ modalTitle }}</h5>
        <div>{{ modalSubtitle }}</div>
      </div>
    </template>
    <div class="messages-container">
      <name-input
        v-if="hasData"
        :all-des="allDes"
        :is-ready="true"
        :name="de.name"
        :de-id="de.id"
        :should-focus="!deName"
        @on-name-input-changed="onDeNameChanged"
      ></name-input>

      <element-description
        :elem-description="de.description"
        @on-element-description-changed="onDeDescriptionChanged"
      ></element-description>
      <div
        class="message-row"
        v-for="(msg, index) in messageList"
        :key="msg.id"
      >
        <div class="msg-left" v-if="!loadingData">
          <label class="field-label">
            {{ categoryText }}
          </label>
          <div class="field-search">
            <search-box
              :id="'cat-search-' + msg.id"
              :has-icon="false"
              :box-type="'deEditModal'"
              :list="categoryResults"
              :value="msg.categoryForDisplay ? msg.categoryForDisplay : null"
              :label-only="true"
              :is-first-input="!msg.isValid && index > 0"
              @on-search-box-item-selected="onCategorySelected"
              @on-search-box-change="onCategoryChange"
            />
          </div>
        </div>

        <div class="msg-right" v-if="msg.category && !loadingData">
          <label class="field-label">
            {{ nameText }}
          </label>
          <div class="field-search">
            <search-box
              :id="'name-search-' + msg.id"
              :has-icon="false"
              :box-type="'deEditModal'"
              :list="nameResults"
              :value="msg.nameForDisplay ? msg.nameForDisplay : null"
              :label-only="true"
              @on-search-box-item-selected="onNameSelected"
              @on-search-box-change="onNameChange"
            />
          </div>
        </div>

        <div v-show="!hasOneMessage">
          <svg
            class="m-close-selection-icon"
            tabindex="0"
            @keydown.enter.stop="confirmDelete(msg.id)"
            @click="confirmDelete(msg.id)"
          >
            <use
              xlink:href="@/assets/icons/utility-sprite/svg/symbols.svg#close"
            ></use>
          </svg>
        </div>
      </div>
      <div class="add-btn-wrapper">
        <button class="modal-btn no-user-select add-btn" @click="addMsg">
          {{ addText }}
        </button>
      </div>
    </div>

    <div class="loader" v-if="loadingData"></div>

    <simple-modal
      ref="simpleModal"
      :show="displaySimpleModal"
      :message="areYouSureText"
      :cancel-text="cancelText"
      :ok-text="okText"
      @on-simple-modal-cancel="onCancelDeleteItem"
      @on-simple-modal-ok="onConfirmDeleteItem"
    />

    <template v-slot:modal-footer>
      <button
        class="modal-btn no-user-select modal-cancel-btn"
        @click="hideModal()"
      >
        {{ cancelText }}
      </button>
      <button
        class="modal-btn no-user-select modal-save-btn"
        @click="finishQuery()"
        :disabled="isDisabled"
      >
        {{ okText }}
      </button>
    </template>
  </b-modal>
</template>

<script>
  import { mapState } from "vuex";
  import SearchBox from "@/components/SearchBox";
  import services from "@/services";
  import { TOAST_OPTIONS, MUTATION_NAME } from "@/static/global-variables";
  import SimpleModal from "@/views/drag-drop-pages/components/modal/SimpleModal";
  import store from "@/store";
  import utils from "@/utils";
  import { v4 as uuidv4 } from "uuid";
  import NameInput from "@/components/NameInput";
  import ElementDescription from "@/components/ElementDescription";

  export default {
    name: "VmsgEdit",
    components: {
      SearchBox,
      SimpleModal,
      NameInput,
      ElementDescription
    },
    computed: {
      ...mapState({
        messages: "messages",
        user: "user"
      }),
      hasOneMessage() {
        return this.messageList.length === 1;
      },
      categoryText() {
        return this.getMessageText(this.messages.APPROVED_NOTES_CATEGORY);
      },
      nameText() {
        return this.getMessageText(this.messages.NAME);
      },
      addText() {
        return this.$t("add");
      },
      okText() {
        return this.getMessageText(this.messages.OK);
      },
      cancelText() {
        return this.getMessageText(this.messages.CANCEL);
      },
      areYouSureText() {
        return this.getMessageText(this.messages.ARE_YOU_SURE);
      },
      sfdcDataErrorText() {
        return this.getMessageText(this.messages.SFDC_DATA_ERROR);
      },
      isDisabled() {
        return (
          utils.hasInvalidElements(this.messageList) ||
          this.loadingData ||
          !this.isNameValid
        );
      },
      hasData() {
        return Boolean(this.dataElement && this.desClone);
      },
      modalId() {
        return `vmsg-edit-modal_${this.de.id}`;
      },
      modalTitle() {
        return `${this.getMessageText(this.messages.Edit)} ${this.$t(
          utils.getDataElementLabel(this.de.type)
        )}`;
      },
      modalSubtitle() {
        return utils.getModalSubtitle(this.de.type);
      }
    },
    props: {
      appId: String,
      de: Object,
      id: String,
      name: String,
      allDes: Object
    },
    data() {
      return {
        loadingData: false,
        dataElement: {},
        displaySimpleModal: false,
        tmpMsgId: "",
        categoryResults: [],
        nameResults: [],
        messageList: [],
        deName: null,
        deDescription: null,
        isNameValid: false,
        desClone: null
      };
    },
    methods: {
      hideModal() {
        if (this.de.notReady) {
          const params = {
            appId: this.de.appId,
            deId: this.de.id
          };
          store.commit(MUTATION_NAME.DELETE_DATA_ELEMENT, params);
        }
        this.$bvModal.hide(this.modalId);
      },
      createMsgPlaceholder() {
        return {
          id: uuidv4(),
          name: null,
          category: null,
          isValid: false
        };
      },
      init() {
        this.deName = this.de.name;
        this.deDescription = this.de.description;
        this.dataElement = JSON.parse(JSON.stringify(this.de));
        this.desClone = JSON.parse(JSON.stringify(this.allDes));
        let messages = this.dataElement.messages;

        if (!messages) {
          this.messageList.push(this.createMsgPlaceholder());
        } else {
          this.messageList = messages;
        }
      },
      onCategorySelected(val) {
        let mId = val.searchBoxId.replace("cat-search-", "");

        this.messageList.forEach(m => {
          if (m.id === mId) {
            if (val.label) {
              m.categoryForDisplay = {
                name: val.label,
                label: val.label
              };
              m.category = val.label;
            } else {
              m.categoryForDisplay = null;
              m.nameForDisplay = null;
              m.category = null;
              m.name = null;
              m.isValid = false;
            }
          }
        });
      },
      onNameSelected(val) {
        let id = val.searchBoxId.replace("name-search-", "");

        this.messageList.forEach(m => {
          if (m.id === id) {
            if (val.label) {
              m.nameForDisplay = {
                name: val.label,
                label: val.label
              };
              m.name = val.label;
              m.isValid = true;
            } else {
              m.nameForDisplay = null;
              m.name = null;
              m.isValid = false;
            }
          }
        });
      },
      sfdcSearchErrorHandler() {
        this.$bvToast.toast(this.sfdcDataErrorText, TOAST_OPTIONS.bottomCenter);
      },

      performCategorySearch(input) {
        this.categoryResults = null;
        services
          .fetchVeevaMsgCategoriesOnly(this.user.language, input)
          .then(resp => {
            if (!resp || !Array.isArray(resp.records)) {
              this.$bvToast.toast(
                this.sfdcDataErrorText,
                TOAST_OPTIONS.bottomCenter
              );
            } else {
              this.categoryResults = resp.records.map(msg => {
                return {
                  label: msg.Category_vod__c
                };
              });
            }
          }, this.sfdcSearchErrorHandler);
      },

      performNameSearch(input, id) {
        this.nameResults = null;
        let msgId = id.replace("name-search-", "");
        const msg = this.messageList.find(m => m.id === msgId);
        services
          .fetchVeevaMsgNames(this.user.language, msg.category, input)
          .then(resp => {
            if (!resp || !Array.isArray(resp.records)) {
              this.$bvToast.toast(
                this.sfdcDataErrorText,
                TOAST_OPTIONS.bottomCenter
              );
            } else {
              this.nameResults = utils.reformatForName(resp.records);
            }
          }, this.sfdcSearchErrorHandler);
      },
      onCategoryChange(input) {
        this.performCategorySearch(input);
      },

      onNameChange(input, id) {
        this.performNameSearch(input, id);
      },

      addMsg() {
        this.messageList.push(this.createMsgPlaceholder());
      },

      onCancelDeleteItem() {
        this.displaySimpleModal = false;
      },
      deleteMsg(id) {
        this.$nextTick(() => {
          this.messageList = this.messageList.filter(msg => {
            return msg.id !== id;
          });
        });
      },
      onConfirmDeleteItem() {
        this.displaySimpleModal = false;
        this.deleteMsg(this.tmpMsgId);
        // reset
        this.tmpMsgId = null;
      },

      confirmDelete(id) {
        this.tmpMsgId = id;
        this.displaySimpleModal = true;
      },

      finishQuery() {
        let de = {
          id: this.dataElement.id,
          type: this.dataElement.type,
          description: this.deDescription,
          name: this.deName,
          messages: this.messageList
        };

        let deInfo = {
          appId: this.appId,
          de
        };
        store.commit(MUTATION_NAME.UPDATE_DE_QUERY, deInfo);
        this.$bvModal.hide(this.modalId);
      },
      onDeNameChanged(isValidName, nameInput) {
        this.deName = nameInput;
        this.isNameValid = isValidName;
      },
      onDeDescriptionChanged(input) {
        this.deDescription = input;
      }
    }
  };
</script>

<style scoped>
  label {
    margin-bottom: 0;
  }

  .message-row {
    display: flex;
    padding: 0.25rem 1rem;
  }

  .msg-left,
  .msg-right {
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: space-between;
  }

  .msg-right {
    padding-left: 1rem;
  }

  .field-search {
    flex-basis: 75%;
  }

  .m-close-selection-icon {
    margin-left: 0.5rem;
    width: 2rem;
    height: 2rem;
    fill: #706e6b;
    cursor: pointer;
    padding: 0.45rem;
  }

  .add-btn {
    margin-left: 1rem;
  }

  .msg-left .field-label {
    min-width: 6rem;
  }

  .msg-left .field-search {
    flex-basis: 100%;
  }
</style>
